import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// mui components
import { MenuItem, PopperPlacementType, MenuItemProps } from '@mui/material';
import { styled } from '@mui/system';

import PopperButtonNewUI from './new-ui';
import { CSSProperties } from '@mui/styles';

interface IStyledMenuItem extends MenuItemProps {
  iconSize?: number;
}

const StyledMenuItemNewUI = styled(({ iconSize, ...props }: IStyledMenuItem) => (
  <MenuItem {...props} />
))(({ iconSize }) => ({
  padding: '14px 16px',
  fontSize: '14px',
  lineHeight: '140%',
  fontWeight: 400,
  color: '#23252F',
  fontFamily: 'Inter, sans-serif !important',
  justifyContent: 'space-between',
  '&:hover': {
    borderRadius: '8px',
    backgroundColor: '#F2F5FF',
  },
  '&.Mui-disabled': {
    opacity: 1,
  },
  '& a': {
    display: 'flex',
    alignItems: 'center',
    color: '#535F77',
    textDecoration: 'unset',
  },
  '& svg': {
    width: iconSize ? `${iconSize}px` : '24px',
    height: iconSize ? `${iconSize}px` : '24px',
  },
}));

const errorStyles = {
  backgroundColor: '#F44336',
  color: '#fff',
  '&:hover': {
    backgroundColor: '#f02e20',
    color: '#fff',
  },
};

export interface OwnProps {
  name: string;
  width: string;
  items: {
    label: string;
    isErrorStyles?: boolean;
    value?: string;
    Icon?: any;
    iconSize?: number;
    onClick?: () => void;
    color?: string;
    hide?: boolean;
  }[];
  ButtonComponent: any;
  isBold?: boolean;
  activeItem?: string;
  customPlacement?: PopperPlacementType;
  IconPlacement?: 'start' | 'end';
  className?: string;
  popperStyles?: CSSProperties;
}

const PopperButton: React.FC<OwnProps> = ({
  name,
  width,
  items,
  ButtonComponent,
  isBold,
  activeItem,
  customPlacement = 'bottom-end',
  IconPlacement = 'start',
  className,
  popperStyles,
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const commonItemStyles = useMemo(
    () => ({
      fontWeight: isBold ? 'bold' : 'normal',
    }),
    [isBold]
  );

  const handleToggle = useCallback(() => {
    setOpen((prevOpen) => !prevOpen);
  }, []);

  const handleClose = useCallback((event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  }, []);

  const handleListKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }, []);

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = useRef(open);

  useEffect(() => {
    if (prevOpen.current && !open) {
      anchorRef.current!?.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const props = {
    width,
    items,
    activeItem,
    ButtonComponent,
    anchorRef,
    name,
    open,
    errorStyles,
    commonItemStyles,
    customPlacement,
    IconPlacement,
    className,
    handleToggle,
    handleListKeyDown,
    handleClose,
    popperStyles,
  };

  return <PopperButtonNewUI {...props} StyledMenuItem={StyledMenuItemNewUI} />;
};

export default PopperButton;
